import {useIntl} from 'react-intl';
import React, {useEffect, useState} from 'react';
import {ComboBox, DeprecatedCreativeModal as CreativeModal, Text, TextField} from '@forecast-it/design-system';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import DirectApi from '../../../directApi';
import {createFragmentContainer, graphql} from 'react-relay';
import {SageIntacctCreateNewProjectModal_company} from './__generated__/SageIntacctCreateNewProjectModal_company.graphql';
import CreateSageIntacctProjectMutation from '../../../mutations/CreateSageIntacctProjectMutation';
import {hideLoader, showLoader} from '../../global_loader';
import styled from 'styled-components/macro';
import Util from '../../../forecast-app/shared/util/util';
import {HorizontalBar as DefaultHorizontalBar} from './SageIntacctModal.styled';

type SageIntacctCreateNewProjectModalProps = {
	closeModal: () => void;
	forecastProjectId: string;
	forecastProjectName: string;
	company: SageIntacctCreateNewProjectModal_company;
};

type SageIntacctProjectCategory = {
	id: string;
};

type ForecastClient = {
	id: number;
	name: string;
	sageIntacctCustomerId: string;
};

type SageIntacctLocation = {
	LOCATIONID: string;
	NAME: string;
	RECORDNO: number;
};

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
const HorizontalBar = styled(DefaultHorizontalBar as any)`
	margin: 8px -24px -18px;
`;

const SageIntacctCreateNewProjectModal = ({
	closeModal,
	forecastProjectId,
	forecastProjectName,
	company,
}: SageIntacctCreateNewProjectModalProps): JSX.Element => {
	// Hardcoded project categories. Sage Intacct does not have an endpoint to retrieve these.
	const projectCategories: SageIntacctProjectCategory[] = [
		{id: 'Contract'},
		{id: 'Capitalized'},
		{id: 'Internal non-billable'},
		{id: 'Internal billable'},
	];

	const {formatMessage} = useIntl();
	const [locations, setLocations] = useState<SageIntacctLocation[]>([]);
	const [clients, setClients] = useState<ForecastClient[]>([]);
	const [fetching, setFetching] = useState<boolean>(true);
	const [failed, setFailed] = useState<boolean>(false);
	const [projectName, setProjectName] = useState<string>(forecastProjectName);
	const [selectedProjectCategory, setSelectedProjectCategory] = useState<string | null>(null);
	const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
	const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
	const [creating, setCreating] = useState<boolean>(false);
	const disableCreateButton = creating || fetching || !projectName || !selectedProjectCategory;

	useEffect(() => {
		if (company?.clients?.edges && Array.isArray(company.clients.edges)) {
			const companyClients = company.clients.edges.map(edge => edge.node).filter(client => client.sageIntacctCustomerId);
			setClients(companyClients);
		}
	}, []);

	useEffect(() => {
		DirectApi.Fetch('sageIntacct/locations').then(res => {
			if (res?.locations) {
				setLocations(res.locations);
				setFetching(false);
			} else if (res?.error || res?.status === 500 || res?.statusCode === 500) {
				setFailed(true);
				setFetching(false);
			} else {
				setLocations([]);
				setFetching(false);
			}
		});
	}, []);

	const onCreateClick = () => {
		setCreating(true);
		showLoader();
		Util.CommitMutation(
			CreateSageIntacctProjectMutation,
			{
				name: projectName,
				projectCategory: selectedProjectCategory,
				clientId: selectedClientId,
				locationId: selectedLocationId,
				forecastProjectId: forecastProjectId,
			},
			() => {
				setCreating(false);
				hideLoader();

				closeModal();
				showModal({
					type: MODAL_TYPE.SAGE_INTACCT_EXPORT_TASKS,
					forecastProjectId: forecastProjectId,
					isPartOfProjectSyncFlow: true,
				});
			}
		);
	};

	return (
		<CreativeModal
			title={'Create New Project in Sage Intacct'}
			size={'l'}
			closeModal={closeModal}
			creativeButtonText={'Create New Project in Sage Intacct'}
			cancelButtonText={formatMessage({id: 'common.cancel'})}
			onCreateHandler={onCreateClick}
			onCancelHandler={closeModal}
			disabled={disableCreateButton}
			replicateDesignSystem
		>
			{failed && <Text color={'error'}>Something has gone wrong, try again or contact support.</Text>}

			<TextField
				label="Project name"
				onValueChange={setProjectName}
				value={projectName}
				placeholder="Project name"
				size="large"
				width="flex"
			/>

			<ComboBox
				label="Project category"
				data-cy="combobox-project-category"
				defaultItems={projectCategories}
				value={selectedProjectCategory}
				onChange={setSelectedProjectCategory}
				width={ComboBox.WIDTH.FLEX}
			>
				{(projectCategory: SageIntacctProjectCategory) => (
					<ComboBox.Item textValue={projectCategory.id}>
						<Text>{projectCategory.id}</Text>
					</ComboBox.Item>
				)}
			</ComboBox>

			<ComboBox
				label="Client"
				data-cy="combobox-client"
				defaultItems={clients}
				value={selectedClientId}
				onChange={setSelectedClientId}
				width={ComboBox.WIDTH.FLEX}
			>
				{(client: ForecastClient) => (
					<ComboBox.Item key={client.sageIntacctCustomerId} textValue={client.name}>
						<Text>{client.name}</Text>
					</ComboBox.Item>
				)}
			</ComboBox>

			<ComboBox
				label="Location"
				data-cy="combobox-location"
				defaultItems={locations}
				value={selectedLocationId}
				onChange={setSelectedLocationId}
				width={ComboBox.WIDTH.FLEX}
				disabled={fetching}
			>
				{(location: SageIntacctLocation) => (
					<ComboBox.Item key={location.LOCATIONID} textValue={location.NAME}>
						<Text>{location.NAME}</Text>
					</ComboBox.Item>
				)}
			</ComboBox>
			<HorizontalBar />
		</CreativeModal>
	);
};
const sageIntacctCreateNewProjectModalQuery = graphql`
	query SageIntacctCreateNewProjectModal_Query {
		viewer {
			actualPersonId
			component(name: "sage_intacct_new_project_modal")
			company {
				...SageIntacctCreateNewProjectModal_company
			}
		}
	}
`;

export {sageIntacctCreateNewProjectModalQuery};

export default createFragmentContainer(SageIntacctCreateNewProjectModal, {
	company: graphql`
		fragment SageIntacctCreateNewProjectModal_company on Company {
			clients {
				edges {
					node {
						id
						name
						sageIntacctCustomerId
					}
				}
			}
		}
	`,
});
